/*
 * Copyright © 2022-2026 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link, Redirect, useParams } from 'react-router-dom';
import { useLinkStyles } from 'components/common/useLinkStyles';
import WelcomeSection from '../../components/welcomeSection/WelcomeSection';
import useEPIframe from './useEPIframe';
import EPErrorPage from './EPErrorPage';
import { RoutePath } from '../../shared/enums/RoutePath';
import { callApi } from '../../api/helpers/api.helpers';
import draftApi from '../../api/draft.api';

const useStyles = makeStyles({
  iframe: {
    width: '100%',
    minHeight: 'calc(100vh - 10.1875rem)',
  },
});

export default function EPIframe() {
  const { t } = useTranslation();
  const classes = useStyles();
  const linkClass = useLinkStyles();
  const { procedure, submissionId } = useParams();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (!submissionId) return;
    (async () => {
      const submission = await callApi(draftApi.getById)(submissionId);
      if (submission && submission.status !== 'DRAFT') {
        setShouldRedirect(true);
      }
    })();
  }, [submissionId]);
  document.title = t('ep.submissions.page.title', { defaultValue: 'EPO online filing' });

  const { epURL, loadingError, iframeListener } = useEPIframe({ procedure });
  const iframe = useRef();
  const iframeElem = iframe.current;

  useEffect(() => {
    if (iframeElem) {
      window.addEventListener('message', iframeListener);
      return () => window.removeEventListener('message', iframeListener);
    }
    return () => {};
  }, [iframeElem]);

  const breadcrumbTo = RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
    procedure,
  });

  if (shouldRedirect) {
    const path = RoutePath.generateRoutePath(RoutePath.PROCEDURE_RECEIPTS_SUBMISSION, { submissionId, procedure });
    return <Redirect to={path} push={false} />;
  }

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" className="breadcrumbs">
        <Link className={linkClass.link} to={breadcrumbTo}>
          {t('submission.ep.title')}
        </Link>
        <Typography color="textPrimary">{t('submission.EP1001.title.patent')}</Typography>
      </Breadcrumbs>
      <WelcomeSection title={t('draft.EP1001.title')} description={t('draft.EP1001.description')} />
      {!loadingError && <iframe ref={iframe} className={classes.iframe} title={t('draft.EP1001.title')} src={epURL} />}
      {loadingError && <EPErrorPage />}
    </>
  );
}
